import { defineStore } from "pinia";
import { getUserLocale } from "@/fluent";
import httpApi from "@/httpApi";
import { t } from '@/i18n';
import { useUserStore } from "@/stores/userStore";
import config from "@/config";

export const useCorpusStore = defineStore("corpusData", {
  state: () => ({
    queryData: null,
    corpora: [],
    licenses: [
      {tag: "cc-by", name: "CC-BY", url: "https://creativecommons.org/licenses/by/4.0/"},
      {tag: "cc-by-sa", name: "CC-BY-SA", url: "https://creativecommons.org/licenses/by-sa/4.0/"},
      {tag: "cc-by-nc", name: "CC-BY-NC", url: "https://creativecommons.org/licenses/by-nc/4.0/"},
      {tag: "cc-by-nc-sa", name: "CC-BY-NC-SA", url: "https://creativecommons.org/licenses/by-nc-sa/4.0/"},
      {tag: "cc-by-nd", name: "CC-BY-ND", url: "https://creativecommons.org/licenses/by-nd/4.0/"},
      {tag: "cc-by-nc-nd", name: "CC-BY-NC-ND", url: "https://creativecommons.org/licenses/by-nc-nd/4.0/"},
      {tag: "cc-zero", name: "CC-0", url: "https://creativecommons.org/publicdomain/zero/1.0/"},
      {tag: "user-defined", name: "User defined", url: null}
    ],
    languages: [
      { value: "und", name: t('modal-meta-lg-undefined') },
      { value: "en", name: t('modal-meta-lg-english') },
      { value: "de", name: t('modal-meta-lg-german') },
      { value: "fr", name: t('modal-meta-lg-french') },
      { value: "it", name: t('modal-meta-lg-italian') },
      { value: "es", name: t('modal-meta-lg-spanish') },
      { value: "gs", name: t('modal-meta-lg-swiss-german') },
      { value: "rm", name: t('modal-meta-lg-romansh') },
    ],
  }),
  getters: {
    getLicenseByTag: (state) => {
      return (tag) => {
        return state.licenses.find(license => license.tag === tag) || {url: ""}
      }
    },
  },
  actions: {
    async fetchQuery(data) {
      let response = await httpApi.post(`/query`, data)
      this.queryData = await response.data;
      return this.queryData
    },
    saveQuery(data) {
      httpApi.post(`/store`, data).then((response) => {
        return response.data;
      });
    },
    deleteQuery(user_id, room_id, query_id) {
      httpApi.delete(`/user/${user_id}/room/${room_id}/query/${query_id}`).then((response) => {
        return response.data;
      });
    },
    async fetchQueries(data) {
      const userStore = useUserStore()
      await userStore.checkLoaded()
      return httpApi.post(`/fetch`, data).then((response) => {
        this.fetchedQueries = response.data;
        return response.data;
      });
    },
    async updateMeta(data) {
      const lg = getUserLocale().value;
      const corpus = this.corpora.find(c=>c.corpus_id == data.corpusId);
      const descriptions = {};
      // make sure we store meta attributes in meta
      for (let [layer, props] of Object.entries(data.descriptions)) {
        const corpusAttrs = corpus.layer[layer].attributes || {};
        descriptions[layer] = {attributes: {}};
        if ("description" in props)
          descriptions[layer].description = props.description;
        for (let [attr, desc] of Object.entries(props.attributes||{})) {
          if ("meta" in corpusAttrs && attr in corpusAttrs.meta) {
            descriptions[layer].attributes.meta = descriptions[layer].attributes.meta || {};
            descriptions[layer].attributes.meta[attr] = desc;
          }
          else
            descriptions[layer].attributes[attr] = desc;
        }
      }
      const toSend = {lg: lg, metadata: data.metadata, descriptions: descriptions, globals: data.globals}
      if ("projects" in data)
        toSend.projects = data.projects;
      const response = await httpApi.put(`/corpora/${data.corpusId}/meta/update`, toSend);
      return response.data;
    },
    overwriteCorpus(corpusId, toBeOverwritten) {
      httpApi.put(`/corpora/${corpusId}/overwrite`, {overwrite: toBeOverwritten}).then((response) => {
        return response.data;
      });
    },
    async getCorpus(corpusId) {
      const userStore = useUserStore()
      await userStore.checkLoaded()
      return httpApi.get(`/corpora/${corpusId}`);
    },
    requestInvite(corpusId) {
      return httpApi.post(`/corpora/${corpusId}/request`);
    },
    discardInvites(corpusId) {
      return httpApi.delete(`/corpora/${corpusId}/request/discard`);
    },
    fetchCorpora() {
      httpApi.post(`/corpora`).then((response) => {
        this.corporaJson = response.data;
        delete this.corporaJson.config["-1"];
        const lg = getUserLocale().value;
        this.corpora = Object.keys(this.corporaJson.config).map(corpusId => {
          let corpus = this.corporaJson.config[corpusId]
          corpus.meta['id'] = corpusId
          for (let [k,v] of Object.entries(corpus.meta)) {
            if (typeof(v) == "string" || !(v instanceof Object))
              continue;
            if (lg in v && typeof(v[lg]) == "string")
              corpus.meta[k] = v[lg];
            else if ("en" in v && typeof(v.en) == "string")
              corpus.meta[k] = v.en;
          }
          for (let [layer, props] of Object.entries(corpus.layer)) {
            for (let [k,v] of Object.entries(props.attributes || {})) {
              if (k == "meta" && v instanceof Object) {
                for (let [mk,mv] of Object.entries(v)) {
                  if (!mv.description || typeof(mv.description) == "string" || !(mv.description instanceof Object))
                    continue;
                  if (lg in mv.description && typeof(mv.description[lg]) == "string")
                    corpus.layer[layer].attributes.meta[mk].description = mv.description[lg];
                  else if ("en" in mv.description && typeof(mv.description.en) == "string")
                    corpus.layer[layer].attributes.meta[mk].description = mv.description.en;
                }
              }
              else {
                if (!v.description || typeof(v.description) == "string" || !(v.description instanceof Object))
                  continue;
                if (lg in v.description && typeof(v.description[lg]) == "string")
                  corpus.layer[layer].attributes[k].description = v.description[lg];
                else if ("en" in v.description && typeof(v.description.en) == "string")
                  corpus.layer[layer].attributes[k].description = v.description.en;
              }
            }
          }
          return corpus
        })
      });
    },
    fetchDocuments(data) {
      httpApi.post(`/document_ids/${data.corpora_id}`, data).then((response) => {
        return response.data;
      });
    },
    fetchDocument(data) {
      httpApi.post(`/document/${data.doc_id}`, data).then((response) => {
        return response.data;
      });
    },
    fetchAnnotations(data) {
      if (!(data.anchor in {stream: 1, time: 1}) && !(data.range instanceof Array)) return;
      httpApi.post("/annotations", data).then((response) => {
        return response.data;
      });
    },
    fetchImageAnnotations(data) {
      if (!(data.ids instanceof Array) && !(data.xy_box instanceof Array)) return;
      httpApi.post("/image_annotations", data).then((response) => {
        return response.data;
      });
    },
    clipMedia(data) {
      const {document} = data;
      if (data.span[1]-data.span[0] < 0.1) return;
      httpApi.post(`/document/${document}/clip`, data).then((response) => {
        return response.data;
      });
    },
    async getClipMedia(data) {
      const {file} = data;
      const res = await httpApi.get(`/document/get/clip/${file}`, {responseType: 'blob'});
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clip.zip');
      document.body.appendChild(link);
      link.click();
    },
    async fetchExport(info) {
      const ampsInfo = Object.entries(info).map(([k,v])=>encodeURIComponent(k)+"="+encodeURIComponent(v)).join("&")
      let url = `${httpApi.getUri()}/download_export?${ampsInfo}`;
      if (info.format == "swissdox")
        url = config.appLinks.swissdoxviz;
      const a = document.createElement("A");
      a.target = "_blank";
      a.href = url;
      a.download = "results";
      document.body.append(a);
      a.click();
      a.remove();
    },
  },
});
